import React from "react";
import {
    Box,
    Card,
    CardContent,
    Chip,
    Divider,
    IconButton,
    Modal,
    ToggleButton,
    ToggleButtonGroup,
    Typography,
} from "@mui/material";

import CloseIcon from "@mui/icons-material/Close";
import DesktopWindowsIcon from "@mui/icons-material/DesktopWindows";
import PhoneIphoneIcon from "@mui/icons-material/PhoneIphone";
import AndroidIcon from "@mui/icons-material/Android";

import { CmsBanner } from "../../pages/admin/cms/types";

type Platform = "web" | "ios" | "android";

interface VistaPreviaBannerProps {
    open: boolean;
    previewBanner: CmsBanner | null;
    platform: Platform;
    setPlatform: React.Dispatch<React.SetStateAction<Platform>>;
    onClose: () => void;
}

const tipoColor = (tipo: string) => {
    switch (tipo) {
        case "banner":
            return "primary";
        case "noticias":
            return "secondary";
        case "urgente":
            return "error";
        default:
            return "default";
    }
};

const VistaPreviaBanner: React.FC<VistaPreviaBannerProps> = ({
    open,
    previewBanner,
    platform,
    setPlatform,
    onClose,
}) => {
    React.useEffect(() => {
        if (previewBanner) {
            console.log("URL Banner:", previewBanner.urlBanner);
        }
    }, [previewBanner]);
    if (!previewBanner) return null;

    const esImagen = previewBanner.formato?.toUpperCase() === "IMAGEN";
    const esVideo = previewBanner.formato?.toUpperCase() === "VIDEO";
    const esTexto = previewBanner.formato?.toUpperCase() === "TEXTO";

    return (
        <Modal open={open} onClose={onClose}>
            <Box
                sx={{
                    position: "absolute",
                    top: "50%",
                    left: "50%",
                    transform: "translate(-50%, -50%)",
                    width: "90%",
                    maxWidth: 900,
                    maxHeight: "90vh",
                    overflow: "auto",
                    bgcolor: "background.paper",
                    borderRadius: 2,
                    boxShadow: 24,
                    p: 3,
                }}
            >
                <Box
                    display="flex"
                    justifyContent="space-between"
                    alignItems="center"
                    mb={2}
                >
                    <Typography variant="h6" fontWeight="bold">
                        Vista previa
                    </Typography>

                    <IconButton onClick={onClose}>
                        <CloseIcon />
                    </IconButton>
                </Box>

                <Box display="flex" justifyContent="center" mb={2}>
                    <ToggleButtonGroup
                        value={platform}
                        exclusive
                        onChange={(_, value) => value && setPlatform(value)}
                    >
                        <ToggleButton value="web">
                            <DesktopWindowsIcon sx={{ mr: 1 }} />
                            Web
                        </ToggleButton>

                        <ToggleButton value="ios">
                            <PhoneIphoneIcon sx={{ mr: 1 }} />
                            iOS
                        </ToggleButton>

                        <ToggleButton value="android">
                            <AndroidIcon sx={{ mr: 1 }} />
                            Android
                        </ToggleButton>
                    </ToggleButtonGroup>
                </Box>

                <Divider sx={{ mb: 3 }} />

                {platform === "web" && (
                    <Box>
                        <Typography
                            variant="caption"
                            color="text.secondary"
                            display="block"
                            mb={1}
                        >
                            Vista Previa
                        </Typography>

                        {/*IMAGEN */}

                        {esImagen && previewBanner.urlBanner && (
                            <Box
                                component="img"
                                src={previewBanner.urlBanner}
                                alt={previewBanner.titulo || "Vista previa"}
                                sx={{
                                    width: "100%",
                                    maxHeight: 350,
                                    objectFit: "contain",
                                    borderRadius: 2,
                                    border: "1px solid #ddd",
                                    display: "block",
                                    mb: 2,
                                }}
                            />
                        )}


                        {/*VIDEO*/}

                        {esVideo && previewBanner.urlBanner && (
                            <Box
                                component="video"
                                src={previewBanner.urlBanner}
                                controls
                                preload="metadata"
                                sx={{
                                    width: "100%",
                                    maxHeight: 350,
                                    objectFit: "contain",
                                    borderRadius: 2,
                                    border: "1px solid #ddd",
                                    backgroundColor: "#000",
                                    display: "block",
                                    mb: 2,
                                }}
                            />
                        )}

                        <Card variant="outlined">
                            <CardContent>
                                <Chip
                                    label={previewBanner.tipoVista}
                                    color="primary"
                                    size="small"
                                    sx={{ mb: 1 }}
                                />

                                <Typography fontWeight="bold">
                                    {previewBanner.titulo}
                                </Typography>

                                <Typography color="text.secondary">
                                    {previewBanner.descripcion}
                                </Typography>
                            </CardContent>
                        </Card>
                    </Box>
                )}

                {platform === "ios" && (
                    <Box sx={{ width: 300, mx: "auto" }}>
                        <Box
                            sx={{
                                border: "4px solid #333",
                                borderRadius: 5,
                                overflow: "hidden",
                            }}
                        >
                            {/* IMAGEN */}

                            {esImagen && previewBanner.urlBanner && (
                                <Box
                                    component="img"
                                    src={previewBanner.urlBanner}
                                    alt={previewBanner.titulo || "Vista previa"}
                                    sx={{
                                        width: "100%",
                                        height: 180,
                                        objectFit: "cover",
                                        display: "block",
                                    }}
                                />
                            )}


                            {/* VIDEO */}

                            {esVideo && previewBanner.urlBanner && (
                                <Box
                                    component="video"
                                    src={previewBanner.urlBanner}
                                    controls
                                    preload="metadata"
                                    sx={{
                                        width: "100%",
                                        height: 180,
                                        objectFit: "contain",
                                        backgroundColor: "#000",
                                        display: "block",
                                    }}
                                />
                            )}

                            <Box p={2}>
                                <Chip
                                    label={previewBanner.tipo}
                                    size="small"
                                    color={tipoColor(previewBanner.tipo)}
                                    sx={{ mb: 1 }}
                                />

                                <Typography fontWeight="bold">
                                    {previewBanner.titulo}
                                </Typography>

                                <Typography variant="body2">
                                    {previewBanner.descripcion}
                                </Typography>
                            </Box>
                        </Box>
                    </Box>
                )}

                {platform === "android" && (
                    <Box sx={{ width: 300, mx: "auto" }}>
                        <Box
                            sx={{
                                border: "4px solid #333",
                                borderRadius: 2,
                                overflow: "hidden",
                            }}
                        >
                            {/* IMAGEN */}

                            {esImagen && previewBanner.urlBanner && (
                                <Box
                                    component="img"
                                    src={previewBanner.urlBanner}
                                    alt={previewBanner.titulo || "Vista previa"}
                                    sx={{
                                        width: "100%",
                                        height: 180,
                                        objectFit: "cover",
                                        display: "block",
                                    }}
                                />
                            )}


                            {/* VIDEO */}

                            {esVideo && previewBanner.urlBanner && (
                                <Box
                                    component="video"
                                    src={previewBanner.urlBanner}
                                    controls
                                    preload="metadata"
                                    sx={{
                                        width: "100%",
                                        height: 180,
                                        objectFit: "contain",
                                        backgroundColor: "#000",
                                        display: "block",
                                    }}
                                />
                            )}

                            <Box p={2}>
                                <Chip
                                    label={previewBanner.tipo}
                                    size="small"
                                    color={tipoColor(previewBanner.tipo)}
                                    sx={{ mb: 1 }}
                                />

                                <Typography fontWeight="bold">
                                    {previewBanner.titulo}
                                </Typography>

                                <Typography variant="body2">
                                    {previewBanner.descripcion}
                                </Typography>
                            </Box>
                        </Box>
                    </Box>
                )}

                <Divider sx={{ my: 3 }} />

                <Typography variant="body2">
                    <strong>Tipo de Aplicacion:</strong> {previewBanner.tipoAplicacion}
                </Typography>

                <Typography variant="body2">
                    <strong>Seccion:</strong> {previewBanner.seccion}
                </Typography>

                <Typography variant="body2">
                    <strong>Categoria:</strong> {previewBanner.tipo}
                </Typography>

                <Typography variant="body2">
                    <strong>Tipo de Vista:</strong> {previewBanner.tipoVista}
                </Typography>

                <Typography variant="body2">
                    <strong>Formato:</strong>{" "}
                    {previewBanner.formato || "N/A"}
                </Typography>

                <Typography variant="body2">
                    <strong>Estado:</strong>{" "}
                    {previewBanner.activo ? "Activo" : "Inactivo"}
                </Typography>

                {previewBanner.urlRedirect && (
                    <Typography
                        color="primary"
                        sx={{ mt: 2, wordBreak: "break-word" }}
                    >
                        {previewBanner.urlRedirect}
                    </Typography>
                )}
            </Box>
        </Modal>
    );
};

export default VistaPreviaBanner;